import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

interface User {
  email: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string) => Promise<void>;
  logout: () => void;
  sendVerificationCode: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children?: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const sendVerificationCode = async (email: string): Promise<void> => {
    // Check rate limiting (1 minute)
    const lastSentKey = `lastSent_${email}`;
    const lastSent = localStorage.getItem(lastSentKey);
    
    if (lastSent) {
      const timeSinceLastSent = Date.now() - parseInt(lastSent);
      const oneMinute = 60 * 1000;
      
      if (timeSinceLastSent < oneMinute) {
        const remainingSeconds = Math.ceil((oneMinute - timeSinceLastSent) / 1000);
        throw new Error(`Too many requests. Try again in ${remainingSeconds} seconds.`);
      }
    }

    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/auth/send-code', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ email })
      // });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store timestamp for rate limiting
      localStorage.setItem(lastSentKey, Date.now().toString());
      
      // Store code temporarily for demo (remove in production)
      const demoCode = '123456';
      sessionStorage.setItem(`code_${email}`, demoCode);
      sessionStorage.setItem(`codeExpiry_${email}`, (Date.now() + 5 * 60 * 1000).toString());
      
      toast.success('Verification code sent to your email');
    } catch (error) {
      console.error('Failed to send verification code:', error);
      throw error;
    }
  };

  const verifyCode = async (email: string, code: string): Promise<boolean> => {
    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/auth/verify-code', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ email, code })
      // });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Demo verification logic (remove in production)
      const storedCode = sessionStorage.getItem(`code_${email}`);
      const codeExpiry = sessionStorage.getItem(`codeExpiry_${email}`);
      
      if (!storedCode || !codeExpiry) {
        throw new Error('Code expired');
      }
      
      if (Date.now() > parseInt(codeExpiry)) {
        sessionStorage.removeItem(`code_${email}`);
        sessionStorage.removeItem(`codeExpiry_${email}`);
        throw new Error('Code expired');
      }
      
      if (storedCode !== code) {
        throw new Error('Incorrect code');
      }
      
      // Clean up verification code
      sessionStorage.removeItem(`code_${email}`);
      sessionStorage.removeItem(`codeExpiry_${email}`);
      
      // Set user
      const newUser = { email };
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      return true;
    } catch (error) {
      console.error('Failed to verify code:', error);
      throw error;
    }
  };

  const login = async (email: string): Promise<void> => {
    const newUser = { email };
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
    toast.success('Login successful.');
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        sendVerificationCode,
        verifyCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

